/**
 * The wardrobe (ตู้เสื้อผ้า, systems/wardrobe.ts): what the player may change about how they look, apart from the
 * creator's choices (data/appearance.ts). Data only:
 *
 * - **Dye pieces and channels.** A piece of clothing takes a colour per channel: the top its main cloth, its trim,
 *   its second cloth and its metal; the bottom and the shoes their main colour. The client turns a channel into the
 *   3D outfits' dye families (render3d/outfits dye.ts: white, red, maroon, gold, trouser, boot) for the parts worn
 *   in that piece's slots; a part with no such family keeps its colours.
 * - **The palette** offered first: the master's colours (warm white, maroon, red, gold, ink black, dark brown) and
 *   the world's lacquer, jade and glazed blue. Any other colour may be picked too.
 * - **Gear shown.** The equipment slots whose piece can show on the figure, each one the player may hide.
 * - **Saved sets** (ชุดบันทึก): how many.
 */
import { type EquipSlot, isEquipSlot } from './items';
import { savedSkinSlot, SKIN_SLOTS, type SkinSlot } from './skins';

/** A piece that takes dyes. */
export type DyePiece = 'top' | 'bottom' | 'shoes';
export const DYE_PIECES: readonly DyePiece[] = ['top', 'bottom', 'shoes'];

/** A dye channel of a piece. */
export type DyeChannel = 'main' | 'trim' | 'second' | 'metal';

export const DYE_CHANNELS: Readonly<Record<DyePiece, readonly { id: DyeChannel; label: string }[]>> = {
  top: [
    { id: 'main', label: 'สีหลัก' },
    { id: 'trim', label: 'สีขลิบ' },
    { id: 'second', label: 'สีรอง' },
    { id: 'metal', label: 'สีเครื่องประดับ' },
  ],
  bottom: [{ id: 'main', label: 'สีหลัก' }],
  shoes: [{ id: 'main', label: 'สีหลัก' }],
};

export const DYE_PIECE_LABELS: Readonly<Record<DyePiece, string>> = {
  top: 'เสื้อ',
  bottom: 'กางเกงและกระโปรง',
  shoes: 'รองเท้า',
};

/** A dye key: `<piece>/<channel>` (the save's `dye` map's keys). */
export const dyeKey = (piece: DyePiece, channel: DyeChannel): string => `${piece}/${channel}`;

/** Whether a key names a channel some piece has. */
export function isDyeKey(key: string): boolean {
  const [piece, channel] = key.split('/');
  return (
    (DYE_PIECES as readonly string[]).includes(piece ?? '') &&
    DYE_CHANNELS[piece as DyePiece].some((c) => c.id === channel)
  );
}

/** A colour as the save keeps it: '#rrggbb', lower case. */
export const isDyeColor = (v: unknown): v is string => typeof v === 'string' && /^#[0-9a-f]{6}$/.test(v);

/** The palette the dye picker offers first (Thai names). */
export const WUXIA_DYES: readonly { hex: string; name: string }[] = [
  { hex: '#ecdad7', name: 'ขาวนวล' },
  { hex: '#f4f1ea', name: 'ขาวหิมะ' },
  { hex: '#bd2129', name: 'แดงชาด' },
  { hex: '#62202c', name: 'แดงเลือดหมู' },
  { hex: '#d2a255', name: 'ทอง' },
  { hex: '#16130f', name: 'ดำหมึก' },
  { hex: '#4a3328', name: 'น้ำตาลเข้ม' },
  { hex: '#33283a', name: 'ม่วงหม่น' },
  { hex: '#7a4ac0', name: 'ม่วงราตรี' },
  { hex: '#2f5fb0', name: 'น้ำเงินเคลือบ' },
  { hex: '#3a8ae0', name: 'ฟ้าคราม' },
  { hex: '#24704c', name: 'เขียวหยก' },
  { hex: '#7fb069', name: 'เขียวใบไผ่' },
  { hex: '#e07a8a', name: 'ชมพูท้อ' },
  { hex: '#e8a33c', name: 'ส้มอำพัน' },
  { hex: '#8f8a82', name: 'เทาเงิน' },
];

/** The equipment slots whose piece may show on the figure: each one can be hidden in the wardrobe. */
export const GEAR_SHOW_SLOTS: readonly EquipSlot[] = ['head', 'body', 'legs', 'feet', 'weapon'];

/** The wardrobe slot a piece of gear shows in (what a fashion piece there hides). */
export const GEAR_PIECE: Readonly<Partial<Record<EquipSlot, SkinSlot>>> = {
  head: 'hat',
  body: 'top',
  legs: 'bottom',
  feet: 'shoes',
};

/** Saved sets (ชุดบันทึก) a character keeps. */
export const WARDROBE_SETS = 5;
/** Longest name of a saved set (characters). */
export const SET_NAME_MAX = 16;

/** One saved set: the skins worn per slot, what is hidden, the dyes. */
export interface WardrobeSet {
  name: string;
  worn: Partial<Record<SkinSlot, string>>;
  hideFashion: SkinSlot[];
  hideGear: EquipSlot[];
  dye: Record<string, string>;
}

/** The wardrobe's save (`PlayerProgress.wardrobe`): what is hidden now, the dyes now, the saved sets. */
export interface WardrobeSave {
  /** Fashion slots worn but not shown (the gear under them shows instead). */
  hideFashion: SkinSlot[];
  /** Equipment slots whose piece is not shown on the figure. */
  hideGear: EquipSlot[];
  /** `<piece>/<channel>` → '#rrggbb' (`dyeKey`). */
  dye: Record<string, string>;
  /** `WARDROBE_SETS` entries, null where none is saved. */
  sets: (WardrobeSet | null)[];
}

export const newWardrobe = (): WardrobeSave => ({
  hideFashion: [],
  hideGear: [],
  dye: {},
  sets: Array.from({ length: WARDROBE_SETS }, () => null),
});

const isSkinSlot = (v: unknown): v is SkinSlot =>
  typeof v === 'string' && (SKIN_SLOTS as readonly string[]).includes(v);

function parseHides(r: Record<string, unknown>): { hideFashion: SkinSlot[]; hideGear: EquipSlot[] } {
  const list = <T>(v: unknown, ok: (x: unknown) => x is T): T[] =>
    Array.isArray(v) ? [...new Set(v.filter(ok))] : [];
  return {
    hideFashion: list(r.hideFashion, isSkinSlot),
    hideGear: list(r.hideGear, (x): x is EquipSlot => isEquipSlot(x) && GEAR_SHOW_SLOTS.includes(x)),
  };
}

function parseDye(v: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (v && typeof v === 'object')
    for (const [k, c] of Object.entries(v as Record<string, unknown>))
      if (isDyeKey(k) && typeof c === 'string' && isDyeColor(c.toLowerCase())) out[k] = c.toLowerCase();
  return out;
}

/** A saved wardrobe kept within its rules (unknown slots, skins, keys and colours dropped). */
export function parseWardrobe(raw: unknown): WardrobeSave {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const sets = Array.isArray(r.sets) ? r.sets : [];
  return {
    ...parseHides(r),
    dye: parseDye(r.dye),
    sets: Array.from({ length: WARDROBE_SETS }, (_, i): WardrobeSet | null => {
      const s = sets[i];
      if (!s || typeof s !== 'object') return null;
      const o = s as Record<string, unknown>;
      const worn: Partial<Record<SkinSlot, string>> = {};
      const w = o.worn && typeof o.worn === 'object' ? (o.worn as Record<string, unknown>) : {};
      for (const slot of SKIN_SLOTS) {
        const id = w[slot];
        const to = typeof id === 'string' ? savedSkinSlot(slot, id) : null;
        if (to && !worn[to]) worn[to] = id as string;
      }
      return {
        name: typeof o.name === 'string' ? o.name.slice(0, SET_NAME_MAX) : `ชุดที่ ${i + 1}`,
        worn,
        ...parseHides(o),
        dye: parseDye(o.dye),
      };
    }),
  };
}
